import { css } from "@emotion/css";
import {
  Box,
  CircularProgress,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as RefreshIcon } from "../../assets/images/refresh_icon.svg";
import { ReactComponent as RefreshIconDark } from "../../assets/images/refresh_icon_dark.svg";
import { ReactComponent as SettingsIconDark } from "../../assets/images/settings-icon-dark.svg";
import { ReactComponent as SettingsIcon } from "../../assets/images/settings-icon.svg";
import CustomButton from "../../components/CustomButton";
import { HeaderCustomIconButton } from "../../components/CustomIconButton";
import ContentLayout from "../../components/layout-components/ContentLayout";
import Typewriter from "../../components/Typewriter";
import AppContext from "../../config/AppContext";
import { listApplicationSettings } from "../../services/ApplicationSettingsService";
import CommonUtil from "../../util/CommonUtils";
import { sidebarContext, TimesheetContext } from "../../util/context";
import BarChart from "./BarChart";
import DashboardActivityToolBar from "./DashboardActivityToolBar";
import "./DashboardLayout.scss";
import DashboardWorklog from "./DashboardWorklog";
import DepartmentFilter from "./DepartmentFilter";
import MemberFilter from "./MemberFilter";
import MostUsedApps from "./MostUsedApps";
import PieChart from "./PieChart";
import ProjectChart from "./ProjectChart";
import TimesheetWeekPicker from "./TimesheetWeekPicker";
import UserActiveHours from "./UserActiveHours";
import UpgradePopup from "../../components/subscription-component/UpgradePopup";
import RestrictPermissionPopup from "../../components/subscription-component/RestrictPermissionPopup";
import ChangeLogsPopup from "../changelogs/ChangeLogsPopup";
import {
  getUserPreferences,
  updateUserPreferences,
} from "../../services/DashboardService";
import DashboardProjectFilter from "../../components/DashboardProjectFilter";
import {
  getProjectsByLoggedUser,
  listProject,
} from "../../services/ProjectService";

export default function DashboardLayout() {
  const { mode } = useContext(AppContext);
  const theme = useTheme();
  const { t, i18n, ready } = useTranslation();
  const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const [eventsToLog, setEventsToLog] = useState([]);
  const [reload, setReload] = useState(false);
  // const [selectedMemberName, setSelectedMemberName] = useState(t("RAL0409"));
  const [selectedMemberName, setSelectedMemberName] = useState(
    ready ? t("RAL0409") : "Ralvie AI"
  );
  const [memberFilter, setMemberFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState(["All departments"]);
  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);
  const timeRef = useRef();
  const mostUsedAppsRef = useRef();
  const navigate = useNavigate();
  const [generateSummary, setGenerateSummary] = useState(false);
  const [eventStartDate, setEventStartDate] = useState();
  const [eventEndDate, setEventEndDate] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [textSummary, setTextSummary] = useState(undefined);
  const [applicationRules, setApplicationRules] = useState([]);
  const [loggedHours, setLoggedHours] = useState(null);
  const [upgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const [restrictPopupOpen, setRestrictPopupOpen] = useState(false);
  const [changeLogPopupOpen, setChangeLogPopupOpen] = useState(false);
  const [hasVersionChanged, setHasVersionChanged] = useState(false);
  const { subscriptionDetails, setUserPreferences } =
    useContext(sidebarContext);
  const [dashboardTypeByPlan, setDashboardTypeByPlan] = useState("");
  const [userPreference, setUserPreference] = useState(null);
  const [projectFilter, setProjectFilter] = useState("All projects");
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (ready) {
      setSelectedMemberName(t("RAL0409"));
    }
  }, [ready, t, i18n.language]);

  useEffect(() => {
    const fetchUserPreferences = async () => {
      try {
        const userId = CommonUtil.getLoggedUser()?.id;
        const response = await getUserPreferences(userId);
        if (response.data?.code === "RCI0000") {
          setUserPreference(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch user preferences:", error);
      }
    };

    fetchUserPreferences();
  }, []);

  const applicationVersion = userPreference?.applicationVersion;
  const legacyAppVersion = userPreference?.legacyAppVersion;

  useEffect(() => {
    if (applicationVersion !== legacyAppVersion) {
      setChangeLogPopupOpen(true);
      setHasVersionChanged(true);
    }
  }, [applicationVersion, legacyAppVersion]);

  const handleChangeLogConfirm = async () => {
    try {
      const userId = CommonUtil.getLoggedUser()?.id;
      const payload = {
        ...userPreference,
        legacyAppVersion: applicationVersion,
      };
      const response = await updateUserPreferences(userId, payload);
      if (response.data?.code === "RCI0000") {
        setUserPreferences(payload);
      }
      navigate("/change-logs");
    } catch (error) {
      console.error("Failed to update user preferences:", error);
    }
  };

  const handleChangeLogClose = async () => {
    try {
      const userId = CommonUtil.getLoggedUser()?.id;
      const payload = {
        ...userPreference,
        legacyAppVersion: applicationVersion,
      };
      const response = await updateUserPreferences(userId, payload);
      if (response.data?.code === "RCI0000") {
        setUserPreferences(payload);
      }
      setChangeLogPopupOpen(false);
    } catch (error) {
      console.error("Failed to update user preferences:", error);
      setChangeLogPopupOpen(false);
    }
  };

  const location = useLocation();
  const locationFromState = location.state ? location.state.navigateBack : null;

  useEffect(() => {
    if (locationFromState) {
      window.history.pushState(null, "", window.location.pathname);
      const handlePopState = (event) => {
        event.preventDefault();
        window.location.href = "/";
        window.history.pushState(null, "", window.location.pathname);
      };
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [locationFromState]);

  const getPopupTitle = (subscriptionDetails, t, isRestrictPopup = false) => {
    if (CommonUtil.getStatusCondition(subscriptionDetails)) {
      if (isRestrictPopup) {
        return t("RAL0902");
      }
    }
    if (CommonUtil.isPlanCodeDefault(subscriptionDetails)) {
      return t("RAL0850");
    }
    return t("RAL0852");
  };

  const getPopupContent = (subscriptionDetails, t, isRestrictPopup = false) => {
    if (CommonUtil.getStatusCondition(subscriptionDetails)) {
      if (isRestrictPopup) {
        return t("RAL0903");
      }
    }
    if (CommonUtil.isPlanCodeDefault(subscriptionDetails)) {
      return t("RAL0849");
    }
    return t("RAL0851");
  };

  useEffect(() => {
    if (
      subscriptionDetails &&
      typeof subscriptionDetails === "object" &&
      "status" in subscriptionDetails &&
      !["live", "non_renewing"].includes(subscriptionDetails.status)
    ) {
      if (!["OWNER"].includes(CommonUtil.getRoleCode())) {
        setRestrictPopupOpen(true);
        return;
      } else {
        setUpgradePopupOpen(true);
        return;
      }
    } else if (subscriptionDetails && typeof subscriptionDetails === "object") {
      const feature = subscriptionDetails?.features?.find(
        (feature) => feature?.feature?.code === "DASHBOARD"
      );
      const value = feature?.value;
      setDashboardTypeByPlan(value);
    }
  }, [subscriptionDetails]);

  const [dashboardFilter, setDashboardFilter] = useState({
    userId: "All members",
    startDate: dayjs().startOf("week").subtract(1, "day").toDate(),
    endDate: dayjs().endOf("week").toDate(),
    departmentFilter: ["All departments"],
    formattedStartTime: "",
    formattedEndTime: "",
  });

  useEffect(() => {
    if (CommonUtil.isEmptyString(selectedMemberName)) {
      setSelectedMemberName(t("RAL0409"));
    }
  }, [i18n.language, t]);

  useEffect(() => {
    if (
      !CommonUtil.isEmptyString(dashboardTypeByPlan) &&
      (dashboardTypeByPlan === "Members Dashboard" ||
        !CommonUtil.isOwnerOrAdminOrTeamleadOrManagerOrHRManager())
    ) {
      setMemberFilter(CommonUtil.getLoggedUser()?.id);
      setDashboardFilter({
        ...dashboardFilter,
        userId: CommonUtil.getLoggedUser()?.id,
      });
    } else if (!CommonUtil.isEmptyString(dashboardTypeByPlan)) {
      setMemberFilter("All members");
    }
  }, [dashboardTypeByPlan]);

  useEffect(() => {
    listApplicationSettings().then((res) => {
      if (
        res.data?.code === "RCI0000" &&
        res.data?.data?.applicationRules instanceof Array
      ) {
        setApplicationRules(res.data?.data?.applicationRules);
      }
    });
  }, []);

  const [filter, setFilter] = useState({
    logged: true,
    unlogged: true,
    hidden: false,
    title: "",
    selectedDate: dayjs(),
    activityDate: dayjs(),
  });

  const handleRefresh = () => {
    setMemberFilter(
      CommonUtil.isOwnerOrAdminOrTeamleadOrManagerOrHRManager() &&
        dashboardTypeByPlan !== "Members Dashboard"
        ? "All members"
        : CommonUtil.getLoggedUser()?.id
    );
    setDepartmentFilter(["All departments"]);
    setProjectFilter("All projects");
    setSelectedMemberName("Ralvie AI");
    if (timeRef.current) {
      timeRef.current.resetState();
    }
    if (mostUsedAppsRef.current) {
      mostUsedAppsRef.current.resetState();
    }

    setDashboardFilter({
      ...dashboardFilter,
      departmentFilter: ["All departments"],
      userId: "All members",
      startDate: dayjs().startOf("week").subtract(1, "day").toDate(),
      endDate: dayjs().endOf("week").toDate(),
    });

    setFilter({
      logged: true,
      unlogged: true,
      hidden: false,
      title: "",
      selectedDate: dayjs(),
      activityDate: dayjs(),
    });

    setReload(!reload);
  };
  useEffect(() => {
    if (CommonUtil.isEmptyString(selectedMemberName)) {
      setSelectedMemberName(t("RAL0409"));
    }
  }, [i18n.language, t]);

  const handleWeek = (startDate, endDate) => {
    const currentTimestamp = Date.now();
    const oneDayInMillis = 24 * 60 * 60 * 1000;
    const yesterdayTimestamp = currentTimestamp - oneDayInMillis;
    const yesterdayDate = new Date(yesterdayTimestamp);
    const formattedYesterday = `${yesterdayDate.getFullYear()}-${(
      "0" +
      (yesterdayDate.getMonth() + 1)
    ).slice(-2)}-${("0" + yesterdayDate.getDate()).slice(-2)}`;
    const formattedStartDate = CommonUtil.formatDateToUTCTime(
      startDate ? startDate : formattedYesterday
    );
    const formattedEndDate = CommonUtil.formatDateToUTCTime(
      endDate ? endDate : Date.now()
    );
    setEventStartDate(formattedStartDate);
    setEventEndDate(formattedEndDate);
  };

  // useEffect(() => {
  //   handleWeek();
  // }, []);

  const handleDepartmentFilterChange = (newDepartmentFilter) => {
    setDepartmentFilter(newDepartmentFilter);
    let userId = dashboardFilter.userId;

    // Clear member filter when department filter changes
    setDashboardFilter({
      ...dashboardFilter,
      departmentFilter: newDepartmentFilter,
      userId: userId,
    });
  };

  const handleMemberFilterChange = (newMemberFilter) => {
    setMemberFilter(newMemberFilter);
    setDashboardFilter({
      ...dashboardFilter,
      userId: newMemberFilter,
    });
  };

  const handleSettings = () => {
    navigate("/categoriesApps");
  };

  const handleDateChange = (date) => {
    setFilter({ ...filter, selectedDate: date });
    setEventsToLog([]);
  };

  useEffect(() => {
    const fetchProject = async () => {
      try {
        let updatedProjects = [];
        if (CommonUtil.isOwnerOrAdmin()) {
          listProject()
            .then((res) => {
              if (res.data.code !== "RCE0131") {
                updatedProjects = res.data.data.projects?.map((project) => ({
                  id: project?.id,
                  name: project?.name,
                  assignedMembers: project?.assignedMembers || [],
                }));
                updatedProjects?.sort((a, b) => a.name.localeCompare(b.name));
                setProjects(updatedProjects);
              }
            })
            .catch((error) => {
              console.error("Error fetching projects:", error);
            });
        } else {
          const res = await getProjectsByLoggedUser();
          updatedProjects = res?.data?.data?.map((project) => ({
            id: project?.project?.id,
            name: project?.project?.name,
            assignedMembers: project?.project?.assignedMembers || [],
          }));
        }

        updatedProjects?.sort((a, b) => a.name.localeCompare(b.name));
        setProjects(updatedProjects);
      } catch (error) {
        console.error("Error fetching project:", error);
      }
    };

    if (CommonUtil.isOwnerOrAdminOrManagerOrHRManager()) {
      fetchProject();
    }
  }, []);

  const actions = (
    <Box display="flex">
      <TimesheetWeekPicker
        ref={timeRef}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        selectedDay={dayjs()}
      />

      {dashboardTypeByPlan !== "Members Dashboard" &&
        CommonUtil.isOwnerOrAdminOrManagerOrHRManager() && (
          <>
            <Box>
              <DashboardProjectFilter
                projectFilter={projectFilter}
                setProjectFilter={(val) => setProjectFilter(val)}
                projects={projects}
                setProjects={setProjects}
              />
            </Box>
          </>
        )}
      {!CommonUtil.isEmptyString(dashboardTypeByPlan) &&
        dashboardTypeByPlan !== "Members Dashboard" &&
        CommonUtil.isOwnerOrAdminOrTeamleadOrManagerOrHRManager() && (
          <>
            <Box mr={CommonUtil.isTeamLeadOrManagerOrHRManager() ? 2 : 0}>
              <MemberFilter
                memberFilter={memberFilter}
                setMemberFilter={handleMemberFilterChange}
                setSelectedMemberName={setSelectedMemberName}
                departmentFilter={departmentFilter}
                projectFilter={projectFilter}
                projects={projects}
              />
            </Box>
          </>
        )}
      {CommonUtil.isOwnerOrAdmin() && (
        <Box
          item
          pr={2}
          pl={2}
          sx={{
            "& .settings-svg": {
              transition: "color 0.3s ease",
              color: mode === "dark" ? "var(--color-33-50)" : "#4f4f4f",
            },
            "&:hover": {
              "& .hover-text, & .settings-svg": {
                color: mode === "dark" ? "#f8f8f8cc" : "#242424",
              },
            },
          }}
        >
          <HeaderCustomIconButton
            id="btn-dashboard-settings-icon"
            title={t("RAL0167")}
            size={isXlScreen ? "large" : "md"}
            variant="square"
            mode={mode}
            lightMode={
              <SettingsIcon
                id="btn-dashboard-settings-icon"
                height={isXlScreen ? "18px" : "14px"}
                className="settings-svg"
              />
            }
            darkMode={
              <SettingsIconDark
                id="btn-dashboard-settings-icon"
                height={isXlScreen ? "18px" : "14px"}
                className="settings-svg"
              />
            }
            onClick={handleSettings}
          />
        </Box>
      )}
      <Box
        item
        sx={{
          "& .refresh-svg": {
            transition: "color 0.3s ease",
            color: mode === "dark" ? "var(--color-33-50)" : "#242424cc",
          },
          "&:hover": {
            "& .hover-text, & .refresh-svg": {
              color: mode === "dark" ? "#f8f8f8cc" : "#242424",
            },
          },
        }}
      >
        <HeaderCustomIconButton
          id="btn-refresh-dashboard"
          title={t("RAL0169")}
          size={isXlScreen ? "large" : "md"}
          variant="square"
          lightMode={
            <RefreshIcon
              id="btn-refresh-dashboard"
              height={isXlScreen ? "18px" : "14px"}
              className="refresh-svg"
            />
          }
          darkMode={
            <RefreshIconDark
              id="btn-refresh-dashboard"
              height={isXlScreen ? "18px" : "14px"}
              className="refresh-svg"
            />
          }
          onClick={handleRefresh}
        />
      </Box>
    </Box>
  );

  return (
    <TimesheetContext.Provider
      value={{
        reload,
        setReload,
        eventsToLog,
        setEventsToLog,
        applicationRules,
        setApplicationRules,
      }}
    >
      <ContentLayout
        className="DashboardLayout"
        title={
          selectedMemberName?.length > 10 ? (
            <Tooltip
              id="dashboard-header-name"
              title={selectedMemberName}
              placement="bottom"
              PopperProps={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [-200, 0],
                    },
                  },
                ],
              }}
            >
              <Typography
                id="dashboard-header-name"
                className="title-text text-truncate"
              >
                {`${selectedMemberName.substring(0, 10)}...`}
              </Typography>
            </Tooltip>
          ) : (
            <Typography
              id="dashboard-header-text-name"
              className="title-text text-truncate"
            >
              {selectedMemberName}
            </Typography>
          )
        }
        actions={actions}
        bg={"transparent"}
      >
        {CommonUtil.isEmptyString(dashboardTypeByPlan) ||
        CommonUtil.isEmptyString(memberFilter) ? (
          <></>
        ) : (
          <div
            className={css`
              display: flex;
              justify-content: space-between;
              grid-gap: 24px;
              width: calc(100% - 24px);
            `}
          >
            <div
              className={css`
                width: 70%;
                height: 85vh;
                overflow-y: scroll;
                flex: none;
              `}
            >
              <div
                className={css`
                  display: flex;
                  grid-gap: 24px;
                  width: 100%;
                  min-height: 50%;
                  margin-bottom: 24px;
                `}
              >
                {/* PieChart */}
                <div
                  className={css`
                    min-height: 100%;
                    max-height: 100%;
                    width: 50%;
                    background: var(--card-background-color) 0% 0% no-repeat
                      padding-box;
                    border-radius: 5px;
                    opacity: 1;
                    padding: 20px 20px;
                    flex: 0 0 calc(50% - 12px);
                    overflow: hidden;
                  `}
                >
                  <PieChart
                    dashboardFilter={dashboardFilter}
                    userId={memberFilter}
                    startDate={startDate}
                    endDate={endDate}
                    departmentFilter={departmentFilter}
                    projectFilter={projectFilter}
                  />
                </div>
                {/* ProjectChart */}
                <div
                  className={css`
                    width: 50%;
                    background: var(--card-background-color) 0% 0% no-repeat
                      padding-box;
                    border-radius: 5px;
                    opacity: 1;
                    flex: 0 0 calc(50% - 12px);
                    // overflow: hidden;
                  `}
                >
                  {dashboardTypeByPlan !== "Members Dashboard" &&
                    memberFilter === "All members" && (
                      <div
                        className={css`
                          position: relative;
                          height: 95%;
                          width: 100%;
                          background: var(--card-background-color) 0% 0%
                            no-repeat padding-box;
                          border-radius: 5px;
                          opacity: 1;
                        `}
                      >
                        <ProjectChart
                          dashboardTypeByPlan={dashboardTypeByPlan}
                          userId={memberFilter}
                          startDate={startDate}
                          endDate={endDate}
                          departmentFilter={departmentFilter}
                          projectFilter={projectFilter}
                        />
                      </div>
                    )}

                  {(dashboardTypeByPlan === "Members Dashboard" ||
                    memberFilter !== "All members") && (
                    <div
                      className={css`
                        position: relative;
                        height: 95%;
                        width: 100%;
                        background: var(--card-background-color) 0% 0% no-repeat
                          padding-box;
                        border-radius: 5px;
                        opacity: 1;
                      `}
                    >
                      <MostUsedApps
                        dashboardTypeByPlan={dashboardTypeByPlan}
                        ref={mostUsedAppsRef}
                        dashboardFilter={dashboardFilter}
                        userId={memberFilter}
                        startDate={startDate}
                        departmentFilter={departmentFilter}
                        endDate={endDate}
                        projectFilter={projectFilter}
                      />
                    </div>
                  )}
                </div>
              </div>
              {/* BarChart */}
              <div
                className={css`
                  display: flex;
                  grid-gap: 24px;
                  width: 100%;
                  min-height: 50%;
                `}
              >
                <BarChart
                  dashboardFilter={dashboardFilter}
                  userId={memberFilter}
                  startDate={startDate}
                  endDate={endDate}
                  departmentFilter={departmentFilter}
                  projectFilter={projectFilter}
                />
              </div>
              {/* UserActiveHours */}
              {dashboardTypeByPlan !== "Members Dashboard" &&
                CommonUtil.isOwnerOrAdminOrTeamleadOrManagerOrHRManager() &&
                memberFilter === "All members" && (
                  <div
                    className={css`
                      display: flex;
                      grid-gap: 24px;
                      width: 100%;
                      min-height: 50%;
                      margin-top: 24px;
                    `}
                  >
                    <UserActiveHours
                      dashboardTypeByPlan={dashboardTypeByPlan}
                      startDate={startDate}
                      endDate={endDate}
                      departmentFilter={departmentFilter}
                      projectFilter={projectFilter}
                    />
                  </div>
                )}
            </div>
            <div
              className={css`
                position: relative;
                height: 85vh;
                min-width: 30%;
                max-width: 30%;
                overflow-y: auto;
                background: var(--card-background-color) 0% 0% no-repeat
                  padding-box;
                border-radius: 5px;
                opacity: 1;
              `}
            >
              {!CommonUtil.isEmptyString(dashboardTypeByPlan) &&
                !textSummary &&
                memberFilter === "All members" && (
                  <MostUsedApps
                    dashboardTypeByPlan={dashboardTypeByPlan}
                    ref={mostUsedAppsRef}
                    dashboardFilter={dashboardFilter}
                    userId={memberFilter}
                    startDate={startDate}
                    departmentFilter={departmentFilter}
                    endDate={endDate}
                    projectFilter={projectFilter}
                  />
                )}
              {!CommonUtil.isEmptyString(dashboardTypeByPlan) &&
                !textSummary &&
                memberFilter !== "All members" && (
                  <>
                    {/* <DashboardActivityToolBar
                  filter={filter}
                  handleDateChange={handleWeek}
                  startDate={startDate}
                  endDate={endDate}
                  // eventStartDate={eventStartDate}
                  onChange={(date) =>
                    setFilter({ ...filter, activityDate: date })
                  }
                  setGenerateSummary={(state) => setGenerateSummary(state)}
                />
                <DashboardActivityView
                  dashboardFilter={dashboardFilter}
                  isLoading={isLoading}
                  setIsLoading={(state) => setIsLoading(state)}
                  setTextSummary={(summary) => setTextSummary(summary)}
                  generateSummary={generateSummary}
                  userId={memberFilter}
                  zoomLevel={10}
                  filter={filter}
                  startDate={eventStartDate}
                  endDate={eventEndDate}
                  setGenerateSummary={(state) => setGenerateSummary(state)}
                /> */}

                    <DashboardActivityToolBar
                      id="btn-day-clndr"
                      onChange={handleDateChange}
                      selectedDate={filter.selectedDate}
                      setGenerateSummary={(state) => setGenerateSummary(state)}
                      loggedHours={loggedHours}
                    />

                    <DashboardWorklog
                      dashboardFilter={dashboardFilter}
                      isLoading={isLoading}
                      setIsLoading={(state) => setIsLoading(state)}
                      setTextSummary={(summary) => setTextSummary(summary)}
                      generateSummary={generateSummary}
                      userId={memberFilter}
                      zoomLevel={10}
                      filter={filter}
                      startDate={filter.selectedDate}
                      endDate={filter.selectedDate}
                      setGenerateSummary={(state) => setGenerateSummary(state)}
                      setLoggedHours={setLoggedHours}
                      projectFilter={projectFilter}
                    />
                  </>
                )}

              {textSummary && (
                <div
                  className={css`
                    position: absolute;
                    height: 100%;
                    width: 100%;
                    padding: 24px 24px;
                  `}
                >
                  <div
                    className={css`
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      margin-bottom: 16px;
                    `}
                  >
                    <Typography
                      className={css`
                        font: normal normal bold 20px/22px GothamBold !important;
                        letter-spacing: 0px;
                        color: #242424 !important;
                        opacity: 1;
                      `}
                    >
                      {dayjs(eventStartDate).isSame(dayjs(), "day")
                        ? "Today's"
                        : dayjs(eventStartDate)?.format("DD-MM-YY")}
                      {" summary"}
                    </Typography>
                  </div>
                  <div
                    className={css`
                      height: 80%;
                      overflow: auto;
                    `}
                  >
                    <div
                      className={css`
                        height: 100%;
                      `}
                    >
                      {/* <div dangerouslySetInnerHTML={{ __html: textSummary }} /> */}
                      <div>
                        <Typewriter text={textSummary} delay={10} />
                      </div>
                    </div>
                  </div>
                  <Box
                    pt={2}
                    display={"flex"}
                    flexDirection={"row-reverse"}
                    minWidth={"100%"}
                    columnGap={2}
                  >
                    <CustomButton
                      variant="outlined"
                      onClick={() => setTextSummary(undefined)}
                    >
                      Close
                    </CustomButton>
                    <CustomButton
                      variant="contained"
                      onClick={() => {
                        CommonUtil.copyToClipboard(textSummary);
                        setTextSummary(undefined);
                      }}
                    >
                      Copy
                    </CustomButton>
                  </Box>
                </div>
              )}
              {isLoading && (
                <Box
                  sx={{
                    display: isLoading ? "flex" : "none",
                  }}
                >
                  <CircularProgress
                    style={{
                      top: "50%",
                      left: "85%",
                      position: "fixed",
                      zIndex: 999,
                    }}
                  />
                </Box>
              )}
            </div>
          </div>
        )}

        {["OWNER", "MANAGER"].includes(CommonUtil.getRoleCode()) && (
          <UpgradePopup
            open={upgradePopupOpen}
            onClose={() => setUpgradePopupOpen(false)}
            onConfirm={() => {
              if (
                CommonUtil.getRoleCode() === "OWNER" ||
                CommonUtil.getRoleCode() === "MANAGER"
              ) {
                navigate("/subscription-manage");
              } else {
                navigate("/page-not-found");
              }
            }}
            title={getPopupTitle(subscriptionDetails, t)}
            content={getPopupContent(subscriptionDetails, t)}
            cancelText={t("RAL0177")}
            confirmText={t("RAL0848")}
          />
        )}

        {!["OWNER"].includes(CommonUtil.getRoleCode()) && (
          <RestrictPermissionPopup
            open={restrictPopupOpen}
            onClose={() => setRestrictPopupOpen(false)}
            title={getPopupTitle(subscriptionDetails, t, true)}
            content={getPopupContent(subscriptionDetails, t, true)}
            cancelText={t("RAL0177")}
          />
        )}

        <ChangeLogsPopup
          open={changeLogPopupOpen && hasVersionChanged}
          onClose={handleChangeLogClose}
          onConfirm={handleChangeLogConfirm}
          title={`${t("RAL1041")} ${applicationVersion}`}
          content={
            <>
              <p>{t("RAL1042")}</p>
              <ul style={{ paddingLeft: "1.8em" }}>
                <li>
                  {t("RAL1243")}
                  <ul style={{ paddingLeft: "1.5em", marginTop: "0.3em", listStyleType: "circle" }}>
                    <li>{t("RAL1244")}</li>
                    <li>{t("RAL1245")}</li>
                    <li>{t("RAL1246")}</li>
                  </ul>
                </li>
                <li>
                  {t("RAL1247")}
                  <ul style={{ paddingLeft: "1.5em", marginTop: "0.3em", listStyleType: "circle" }}>
                    <li>{t("RAL1248")}</li>
                    <li>{t("RAL1249")}</li>
                  </ul>
                </li>
                <li>
                  {t("RAL1250")}
                  <ul style={{ paddingLeft: "1.5em", marginTop: "0.3em", listStyleType: "circle" }}>
                    <li>{t("RAL1251")}</li>
                    <li>{t("RAL1252")}</li>
                  </ul>
                </li>
              </ul>
            </>
          }
          cancelText={t("RAL1046")}
          confirmText={t("RAL1047")}
        />
      </ContentLayout>
    </TimesheetContext.Provider>
  );
}
